import {setRepositoryVisibility} from 'src/resources/RepositoryResource';
import {
  Button,
  Modal,
  ModalVariant,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from '@patternfly/react-core';
import {useState} from 'react';
import FormError from 'src/components/errors/FormError';
import {addDisplayError} from 'src/resources/ErrorHandling';
import {useQueryClient} from '@tanstack/react-query';

export function ConfirmationModal(props: ConfirmationModalProps) {
  const [err, setErr] = useState<string>();

  const queryClient = useQueryClient();
  const changeVisibility = async () => {
    const visibility = props.makePublic ? 'public' : 'private';
    try {
      // TODO: Could replace this with a 'bulkSetRepoVisibility'
      // function in RepositoryResource in the future
      await Promise.all(
        props.selectedItems.map(async (item) => {
          const [org, ...repoArray] = item.split('/');
          const repo = repoArray.join('/');
          await setRepositoryVisibility(org, repo, visibility);
        }),
      );
      queryClient.invalidateQueries(['organization']);
      props.toggleModal();
      props.selectAllRepos(false);
    } catch (error: any) {
      console.error(error);
      setErr(addDisplayError('Unable to change visibility', error));
    }
  };

  const handleModalConfirm = async () => {
    if (props.handleModalConfirm) {
      props.handleModalConfirm();
      return;
    }
    // This modal should never render if no items have been selected,
    // that should be handled by the parent component. Leaving this check
    // in anyway.
    if (props.selectedItems.length > 0) {
      await changeVisibility();
    } else {
      setErr('No items selected');
    }
  };

  return (
    <Modal
      variant={ModalVariant.small}
      isOpen={props.modalOpen}
      onClose={props.toggleModal}
    >
      <ModalHeader title={props.title} />
      <ModalBody>
        <FormError message={err} setErr={setErr} />
        {props.description}
      </ModalBody>
      <ModalFooter>
        <Button
          key="confirm"
          variant="primary"
          onClick={handleModalConfirm}
          data-testid={props.confirmButtonTestId}
        >
          {props.buttonText}
        </Button>
        <Button key="cancel" variant="link" onClick={props.toggleModal}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
}

type ConfirmationModalProps = {
  title: string;
  description: string;
  modalOpen: boolean;
  buttonText: string;
  toggleModal: () => void;
  selectedItems?: string[];
  makePublic?: boolean;
  selectAllRepos?: (isSelecting) => void;
  handleModalConfirm?: () => void;
  confirmButtonTestId?: string;
};
